import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

const deliveryOrdersAPI = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    headers: { 'Content-Type': 'application/json' },
    paramsSerializer: warehouseParamsSerializer,
});

deliveryOrdersAPI.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return applyWarehouseFilter(config);
    },
    (error) => Promise.reject(error)
);

const BASE_URL = '/delivery-sub-orders';


const deliveryOrderService = {
    async getDeliverySubOrders(params = {}, options = {}) {
        try {
            const url = options.endpoint || BASE_URL;
            const response = await deliveryOrdersAPI.get(url, { params, ...options });
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isCancel(error)) {
                return { success: false, error: 'Request canceled', isCanceled: true };
            }
            return { success: false, error: extractApiError(error) };
        }
    },
    async getDeliverySubOrderById(id, options = {}) {
        try {
            const url = options.endpoint || BASE_URL;
            const response = await deliveryOrdersAPI.get(`${url}/${id}`, options);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async assignSupervisor(id, payload) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/assign-supervisor`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async startLoading(id, payload) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/start-loading`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async completeLoading(id, payload) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/complete-loading`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async generateChallan(id, payload) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/generate-challan`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async sendChallanEmail(id) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/send-challan-email`);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async sendChallanNotification(id, type = 'email') {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/send-challan-notification`, null, { params: { type } });
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async markInTransit(id, payload) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/mark-in-transit`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async markDelivered(id, payload) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/mark-delivered`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    /**
     * Cancel a single delivery sub-order (DO) independently of its siblings.
     * Roles: SUPER_ADMIN / ADMIN / SALES_STAFF. Normal users pre-challan only;
     * SUPER_ADMIN may force-cancel post-challan (voids challan, reverses stock).
     * @param {number|string} id - sub-order id
     * @param {string} [reason] - optional internal remark (operator-only)
     */
    async cancelSubOrder(id, reason) {
        try {
            const body = reason ? { reason } : {};
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/cancel`, body);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async addWeightEntry(id, payload) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/add-weight-entry`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async resetWeightEntries(id) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/reset-weight-entries`);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async updateTruckWeights(id, payload) {
        try {
            const response = await deliveryOrdersAPI.put(`${BASE_URL}/${id}/update-truck-weights`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async editChallan(id, payload) {
        try {
            const response = await deliveryOrdersAPI.put(`${BASE_URL}/${id}/edit-challan`, payload);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async markChallanPrinted(id) {
        try {
            const response = await deliveryOrdersAPI.post(`${BASE_URL}/${id}/mark-challan-printed`);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async exportDeliveryChallans(params = {}) {
        try {
            const response = await deliveryOrdersAPI.get('/delivery-challans/export/download', {
                params,
                responseType: 'blob',
            });
            return { success: true, data: response.data, headers: response.headers };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async getMySubOrders(params = {}) {
        try {
            const response = await deliveryOrdersAPI.get('/my-sub-orders', { params });
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },

    async getSubOrderDetail(id) {
        try {
            const response = await deliveryOrdersAPI.get(`${BASE_URL}/${id}`);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: extractApiError(error) };
        }
    },
};

export default deliveryOrderService;
